// src/components/common/AccessSummaryBanner.tsx
//
// A subtle "what you can see" notice surfaced on Chat / Finance Dashboard /
// Build Your Dashboard so users aren't surprised when their data is silently
// filtered. Reads /api/me once on mount; renders nothing until the response
// is in. Hidden entirely if there's no relevant info to show.

import React, { CSSProperties, useEffect, useState } from "react";
import { Info } from "@phosphor-icons/react";
import { accessService } from "../../services/accessService";

type FinanceScope = {
  mode: "all" | "restricted" | "none";
  summary: string;
  directorates?: string[];
  departments?: string[];
};

type MeResponse = {
  role?: string;
  allowedAgents?: string[];
  financeScope?: FinanceScope;
};

const AGENT_LABEL: Record<string, string> = {
  abs: "ABS Census",
  finance: "Finance",
  assets: "Assets",
};

interface Props {
  // Drives what extra info renders alongside the finance-scope line.
  //   chat    — adds the list of available chat agents
  //   finance — finance scope only (we're already on a finance page)
  //   builder — finance scope only (shown when finance dataset selected)
  kind: "chat" | "finance" | "builder";
  style?: CSSProperties;
}

const wrapStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 12,
  padding: "8px 12px",
  background: "#F3F9FD",
  border: "1px solid #C7E0F4",
  borderRadius: 4,
  fontSize: 12,
  color: "#323130",
  margin: "8px 0",
};

const labelStyle: CSSProperties = {
  color: "#605E5C",
  fontWeight: 600,
};

const valueStyle: CSSProperties = {
  color: "#323130",
};

const dividerStyle: CSSProperties = {
  width: 1,
  height: 14,
  background: "#C7E0F4",
};

export default function AccessSummaryBanner({ kind, style }: Props) {
  const [me, setMe] = useState<MeResponse | null>(null);

  useEffect(() => {
    let alive = true;
    accessService
      .get<MeResponse>("/me")
      .then((r) => {
        if (alive) setMe(r.data);
      })
      .catch(() => {
        // /me failure is non-fatal — banner just stays hidden.
      });
    return () => {
      alive = false;
    };
  }, []);

  if (!me) return null;

  const fs = me.financeScope;
  const showFinance = fs && fs.mode !== "none";
  const showAgents = kind === "chat" && (me.allowedAgents?.length ?? 0) > 0;

  if (!showFinance && !showAgents) return null;

  const agentLabels = (me.allowedAgents ?? [])
    .map((id) => AGENT_LABEL[id] ?? id)
    .join(", ");

  return (
    <div style={{ ...wrapStyle, ...style }}>
      <Info size={14} weight="bold" color="#0078D4" />
      {showAgents && (
        <span>
          <span style={labelStyle}>Available chat agents:</span>{" "}
          <span style={valueStyle}>{agentLabels}</span>
        </span>
      )}
      {showAgents && showFinance && <span style={dividerStyle} />}
      {showFinance && (
        <span>
          <span style={labelStyle}>Finance scope:</span>{" "}
          <span style={valueStyle}>{fs!.summary}</span>
        </span>
      )}
    </div>
  );
}
